/**
 * referral —— 邀请裂变工具（v3.14）。
 *
 * 邀请链路：分享者把带 `?ref=<did>` 的欢迎页链接发给好友 →
 * 好友打开后 WelcomePage 捕获参数并暂存 pending →
 * 好友完成解锁后 MePage 展示「绑定邀请人」卡片，一键发起链上 referral bind →
 * 链上自动行为挖矿奖励（推荐绑定 2 分 × 0.001 = 0.002 DPB，一次永久）。
 */

/** 邀请链接中推荐码的查询参数名。 */
export const REF_QUERY_KEY = 'ref';

/** 本地暂存键：待绑定的邀请人（did 或地址）。 */
export const LS_PENDING_REF = 'mesh_pending_ref';

/** 本地标记键：是否已完成 referral bind（避免重复引导）。 */
export const LS_REFERRAL_BOUND = 'mesh_referral_bound';

/** 从当前 URL 读取 `?ref=` 邀请码；无则返回 null。 */
export function readRefFromUrl(): string | null {
  try {
    const raw = new URLSearchParams(window.location.search).get(REF_QUERY_KEY);
    if (!raw) return null;
    // 归一化：剥离 did: 前缀（did:dpb: 视为未 strip 的非法形态），保留 0x（若有）
    const m = /^(did:)?(0x)?([0-9a-f]{40})$/.exec(raw.trim().toLowerCase());
    if (!m) return null;
    return (m[2] ? '0x' : '') + m[3];
  } catch {
    return null;
  }
}

/** 暂存待绑定邀请人（仅当本地尚未绑定且未存过时写入）。 */
export function savePendingRef(ref: string): void {
  if (localStorage.getItem(LS_REFERRAL_BOUND) === '1') return;
  try {
    localStorage.setItem(LS_PENDING_REF, ref);
  } catch {
    // 存储满忽略
  }
}

/** 读取并清除待绑定邀请人；返回 null 表示没有待绑定项。 */
export function consumePendingRef(): string | null {
  try {
    const v = localStorage.getItem(LS_PENDING_REF);
    if (!v) return null;
    localStorage.removeItem(LS_PENDING_REF);
    return v;
  } catch {
    return null;
  }
}

/** 仅读取（不消费）待绑定邀请人。 */
export function peekPendingRef(): string | null {
  try {
    return localStorage.getItem(LS_PENDING_REF);
  } catch {
    return null;
  }
}

/** 标记本地已完成绑定引导。 */
export function markReferralBound(): void {
  try {
    localStorage.setItem(LS_REFERRAL_BOUND, '1');
    localStorage.removeItem(LS_PENDING_REF);
  } catch {
    // 存储满忽略
  }
}

/** 生成邀请链接（欢迎页带推荐码；did 短链可读）。 */
export function buildInviteUrl(did: string): string {
  // 保留当前 origin + pathname（?ref= 捕获已全局化到 App.tsx，任意落地页均可绑定）
  const base = `${window.location.origin}${window.location.pathname}`;
  const ref = did.toLowerCase().replace(/^did:dpb:/, '');
  return `${base}?${REF_QUERY_KEY}=${encodeURIComponent(ref)}`;
}

/** 推荐码 → 链上地址（补 0x 前缀；若已是完整地址/did 则原样归一化）。 */
export function refToAddress(raw: string): string {
  const v = raw.trim().toLowerCase().replace(/^did:dpb:/, '');
  if (/^0x[0-9a-f]{40}$/.test(v)) return v;
  if (/^[0-9a-f]{40}$/.test(v)) return `0x${v}`;
  return raw;
}

/** 邀请奖励说明文案（与 DP 链 protocol.py 奖励表一致：referral 2 分 × 0.001 DPB/分）。 */
export const REFERRAL_COPY = {
  title: '邀请好友',
  subtitle: '好友通过你的专属链接创建身份，双方永久绑定推荐关系',
  rewardNew: '好友绑定后自动获得推荐绑定奖励（2 分 × 0.001 = 0.002 DPB）',
  rewardOld: '好友消费核销时你享推广佣金（promoter 8%）',
  bindTitle: '绑定邀请人',
  bindDesc: '你通过好友邀请链接进入。绑定后一次永久生效，并自动获得推荐绑定挖矿奖励。',
  bindDone: '已绑定邀请人 ✓',
};
